import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Fingerprint, RefreshCw, Cpu, ListChecks, Link2, AlertCircle,
  CheckCircle2, Trash2, Search, Wifi, WifiOff
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};
const fmtPunchState = (s) => {
  if (s === 0) return 'In';
  if (s === 1) return 'Out';
  if (s === 2) return 'Break-out';
  if (s === 3) return 'Break-in';
  if (s === 4) return 'OT-in';
  if (s === 5) return 'OT-out';
  return s == null ? '-' : String(s);
};
const fmtVerify = (v) => {
  if (v === 1) return 'Fingerprint';
  if (v === 4) return 'Card';
  if (v === 15) return 'Face';
  return v == null ? '-' : `Mode ${v}`;
};

export function AdminBiometricTab({ ctx }) {
  const { users = [], branches = [] } = ctx || {};
  const [tab, setTab] = useState('devices');

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Manual add-device dialog
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [addDeviceForm, setAddDeviceForm] = useState({
    serial_number: '',
    device_name: '',
    branch: '',
  });
  const [addDeviceSaving, setAddDeviceSaving] = useState(false);

  // User-push queue stats + resync
  const [cmdQueueCounts, setCmdQueueCounts] = useState({});
  const [resyncing, setResyncing] = useState(false);
  const fetchCmdQueueCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/biometric/cmd-queue?limit=1`);
      setCmdQueueCounts(r.data?.counts || {});
    } catch { /* silent */ }
  }, []);
  const resyncUsers = async () => {
    if (!window.confirm('Push every active app user to every active biometric device? This is safe to run repeatedly.')) return;
    setResyncing(true);
    try {
      const r = await axios.post(`${API}/biometric/resync-users`);
      toast.success(`Queued ${r.data.commands_queued} command(s) across ${r.data.devices.length} device(s)`);
      fetchCmdQueueCounts();
      // Refresh the roster too so it reflects the newly-queued rows.
      if (rosterOpen) fetchRoster();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resync failed');
    } finally {
      setResyncing(false);
    }
  };

  // Device-roster matrix ("verify users on devices")
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [roster, setRoster] = useState({ devices: [], users: [] });
  const fetchRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const r = await axios.get(`${API}/biometric/user-device-matrix`);
      setRoster(r.data || { devices: [], users: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load roster');
    } finally {
      setRosterLoading(false);
    }
  }, []);
  const toggleRoster = () => {
    const nx = !rosterOpen;
    setRosterOpen(nx);
    if (nx) fetchRoster();
  };

  // Per-user punch trace diagnostic
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceUsername, setTraceUsername] = useState('');
  const [traceLoading, setTraceLoading] = useState(false);
  const [trace, setTrace] = useState(null);
  const runTrace = async () => {
    const q = traceUsername.trim();
    if (!q) { toast.error('Enter a username, PIN, or employee code'); return; }
    setTraceLoading(true);
    try {
      // Try username first; the endpoint accepts several identifier types.
      const params = /^\d+$/.test(q)
        ? { biometric_pin: q, days: 30 }
        : { username: q, days: 30 };
      const r = await axios.get(`${API}/biometric/user-punch-trace`, { params });
      setTrace(r.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trace failed');
    } finally {
      setTraceLoading(false);
    }
  };
  const toggleTrace = () => {
    setTraceOpen(v => !v);
    if (!traceOpen) setTrace(null);
  };

  const [punches, setPunches] = useState([]);
  const [punchesLoading, setPunchesLoading] = useState(false);
  const [punchUserFilter, setPunchUserFilter] = useState('all');
  const [punchDeviceFilter, setPunchDeviceFilter] = useState('all');

  const [queue, setQueue] = useState({ stats: {}, failed: [], recent: [] });
  const [queueLoading, setQueueLoading] = useState(false);
  const [viewPacket, setViewPacket] = useState(null);

  const [mappings, setMappings] = useState([]);
  const [uids, setUids] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingSearch, setMappingSearch] = useState('');
  const [newMapping, setNewMapping] = useState({ biometric_uid: '', user_id: '', device_serial: '' });

  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => { m[u.id] = u; });
    return m;
  }, [users]);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try { const r = await axios.get(`${API}/biometric/devices`); setDevices(r.data.devices || []); }
    catch { toast.error('Failed to load devices'); }
    finally { setDevicesLoading(false); }
  }, []);

  const fetchPunches = useCallback(async () => {
    setPunchesLoading(true);
    try {
      const params = { limit: 200 };
      if (punchUserFilter !== 'all') params.user_id = punchUserFilter;
      if (punchDeviceFilter !== 'all') params.device_serial = punchDeviceFilter;
      const r = await axios.get(`${API}/biometric/punches`, { params });
      setPunches(r.data.punches || []);
    } catch { toast.error('Failed to load punches'); }
    finally { setPunchesLoading(false); }
  }, [punchUserFilter, punchDeviceFilter]);

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true);
    try { const r = await axios.get(`${API}/biometric/queue`); setQueue(r.data); }
    catch { toast.error('Failed to load queue'); }
    finally { setQueueLoading(false); }
  }, []);

  const fetchMappings = useCallback(async () => {
    setMappingsLoading(true);
    try {
      const [m, u] = await Promise.all([
        axios.get(`${API}/biometric/mappings`),
        axios.get(`${API}/biometric/uids`),
      ]);
      setMappings(m.data.mappings || []);
      setUids(u.data.uids || []);
    } catch { toast.error('Failed to load mappings'); }
    finally { setMappingsLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'devices') fetchDevices(); }, [tab, fetchDevices]);
  useEffect(() => { if (tab === 'devices') fetchCmdQueueCounts(); }, [tab, fetchCmdQueueCounts]);
  useEffect(() => { if (tab === 'punches') fetchPunches(); }, [tab, fetchPunches]);
  useEffect(() => { if (tab === 'queue') fetchQueue(); }, [tab, fetchQueue]);
  useEffect(() => { if (tab === 'mappings') fetchMappings(); }, [tab, fetchMappings]);

  // Auto-refresh on devices + punches tabs
  useEffect(() => {
    if (tab !== 'devices' && tab !== 'punches') return undefined;
    const handle = setInterval(() => {
      if (tab === 'devices') fetchDevices();
      else if (tab === 'punches') fetchPunches();
    }, 10000);
    return () => clearInterval(handle);
  }, [tab, fetchDevices, fetchPunches]);

  const renameDevice = async (serial, newName) => {
    try {
      await axios.put(`${API}/biometric/devices/${serial}`, { device_name: newName });
      toast.success('Device renamed');
      fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rename failed'); }
  };

  const toggleDeviceActive = async (d) => {
    try {
      await axios.put(`${API}/biometric/devices/${d.serial_number}`, { is_active: !d.is_active });
      fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const setDeviceBranch = async (serial, branch) => {
    try {
      await axios.put(`${API}/biometric/devices/${serial}`, { branch: branch || null });
      toast.success(branch ? `Branch set to ${branch}` : 'Branch cleared');
      fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set branch'); }
  };

  const submitAddDevice = async () => {
    const serial = addDeviceForm.serial_number.trim();
    if (!serial) { toast.error('Serial number is required'); return; }
    if (!addDeviceForm.branch) { toast.error('Branch is required so punches can be linked to a location'); return; }
    setAddDeviceSaving(true);
    try {
      await axios.post(`${API}/biometric/devices`, {
        serial_number: serial,
        device_name: addDeviceForm.device_name.trim() || serial,
        branch: addDeviceForm.branch,
      });
      toast.success('Device added');
      setAddDeviceOpen(false);
      setAddDeviceForm({ serial_number: '', device_name: '', branch: '' });
      fetchDevices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add device');
    } finally {
      setAddDeviceSaving(false);
    }
  };

  const retryPacket = async (queue_id) => {
    try { await axios.post(`${API}/biometric/queue/retry`, { queue_id }); toast.success('Requeued'); fetchQueue(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const discardPacket = async (queue_id) => {
    if (!window.confirm('Discard this packet? This cannot be undone.')) return;
    try { await axios.delete(`${API}/biometric/queue/${queue_id}`); toast.success('Discarded'); fetchQueue(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Discard failed'); }
  };

  const createMapping = async () => {
    if (!newMapping.biometric_uid || !newMapping.user_id) {
      toast.error('UID and app user are required');
      return;
    }
    try {
      await axios.post(`${API}/biometric/mappings`, {
        biometric_uid: newMapping.biometric_uid,
        user_id: newMapping.user_id,
        device_serial: newMapping.device_serial || null,
      });
      toast.success('Mapping created');
      setNewMapping({ biometric_uid: '', user_id: '', device_serial: '' });
      fetchMappings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create mapping'); }
  };

  const importFromTimetec = async () => {
    try {
      const r = await axios.post(`${API}/biometric/import-from-timetec`);
      toast.success(`Imported ${r.data.created} mapping(s) from TimeTec User IDs (skipped ${r.data.skipped})`);
      // Also re-resolve any historical unmapped punches
      const b = await axios.post(`${API}/biometric/backfill-user-ids`);
      if (b.data.updated > 0) toast.success(`Backfilled user_id on ${b.data.updated} historical punch(es)`);
      fetchMappings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  const backfillUserIds = async () => {
    try {
      const r = await axios.post(`${API}/biometric/backfill-user-ids`);
      toast.success(`Resolved ${r.data.updated} unmapped punch(es)`);
      fetchMappings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
  };

  const deleteMapping = async (id) => {
    if (!window.confirm('Remove this mapping?')) return;
    try { await axios.delete(`${API}/biometric/mappings/${id}`); toast.success('Removed'); fetchMappings(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const quickMapFromUid = (uid) => setNewMapping({
    biometric_uid: uid.biometric_uid,
    user_id: '',
    device_serial: uid.device_serial || '',
  });

  const filteredMappings = useMemo(() => {
    if (!mappingSearch.trim()) return mappings;
    const s = mappingSearch.toLowerCase();
    return mappings.filter(m =>
      (m.biometric_uid || '').toLowerCase().includes(s) ||
      (m.username || '').toLowerCase().includes(s) ||
      (m.device_serial || '').toLowerCase().includes(s)
    );
  }, [mappings, mappingSearch]);

  const pending = queue.stats?.PENDING || 0;
  const failed = queue.stats?.FAILED || 0;

  return (
    <div className="space-y-4" data-testid="admin-biometric-tab">
      <div className="bg-white p-4 rounded-xl border border-stone-200">
        <div className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <Fingerprint className="w-5 h-5" /> Biometric Devices
        </div>
        <div className="text-xs text-stone-500 mt-1">
          ADMS cloud-push listener for ZKTeco / FingerTec / TimeTec devices.
          Device server address: <code className="bg-stone-100 px-1 rounded">{(window?.location?.origin || '').replace(/\/$/, '')}/api</code>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="devices" data-testid="bio-tab-devices">
            <Cpu className="w-3 h-3 mr-1" /> Devices
            {devices.some(d => d.online) && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
          </TabsTrigger>
          <TabsTrigger value="punches" data-testid="bio-tab-punches">
            <ListChecks className="w-3 h-3 mr-1" /> Live Punches
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="bio-tab-queue">
            <AlertCircle className="w-3 h-3 mr-1" /> Sync Queue
            {(pending + failed) > 0 && (
              <span className={`ml-2 inline-flex items-center justify-center text-white text-xs rounded-full w-5 h-5 ${failed ? 'bg-rose-500' : 'bg-amber-500'}`}>
                {pending + failed}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="mappings" data-testid="bio-tab-mappings">
            <Link2 className="w-3 h-3 mr-1" /> User Mapping
            {uids.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-amber-500 text-white text-xs rounded-full w-5 h-5">
                {uids.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ---------------- DEVICES ---------------- */}
        <TabsContent value="devices">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="px-4 py-2 flex items-center justify-between border-b border-stone-200">
              <div className="text-sm text-stone-600 flex items-center gap-3">
                <span>{devices.length} device(s) • auto-refresh 10s</span>
                <span className="text-xs text-stone-400" data-testid="cmd-queue-stats">
                  Push queue:
                  {' '}<span className="text-amber-700">{cmdQueueCounts.pending || 0} pending</span>
                  {' · '}<span className="text-blue-700">{cmdQueueCounts.sent || 0} sent</span>
                  {' · '}<span className="text-emerald-700">{cmdQueueCounts.acked || 0} acked</span>
                  {cmdQueueCounts.failed ? <> {' · '}<span className="text-rose-700">{cmdQueueCounts.failed} failed</span></> : null}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchDevices}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${devicesLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={resyncUsers} disabled={resyncing}
                  data-testid="resync-users-button"
                  title="Queue every active app user for enrolment on every active device">
                  <RefreshCw className={`w-3 h-3 mr-1 ${resyncing ? 'animate-spin' : ''}`} />
                  Push all users
                </Button>
                <Button variant="outline" size="sm" onClick={toggleRoster}
                  data-testid="verify-users-button"
                  title="Show a matrix of which users are confirmed on each device">
                  <RefreshCw className={`w-3 h-3 mr-1 ${rosterLoading ? 'animate-spin' : ''}`} />
                  {rosterOpen ? 'Hide roster' : 'Verify users'}
                </Button>
                <Button variant="outline" size="sm" onClick={toggleTrace}
                  data-testid="trace-user-button"
                  title="Trace a single user's punches — device → bio_punch → attendance_record">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  {traceOpen ? 'Hide trace' : 'Trace user'}
                </Button>
                <Button size="sm" onClick={() => setAddDeviceOpen(true)}
                  data-testid="add-bio-device-button"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  + Add device
                </Button>
              </div>
            </div>

            {/* User-device roster matrix - "Verify users" toggle */}
            {rosterOpen && (
              <div className="border border-stone-200 rounded-lg overflow-hidden mb-3" data-testid="user-device-matrix">
                <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex flex-wrap items-center gap-3">
                  <b>Users on devices</b>
                  <span>Rows = users · Columns = devices. Green = confirmed loaded, amber = still pending an ACK from the device, rose = failed, stone = never queued (click "Push all users" to fix).</span>
                  {rosterLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
                </div>
                {roster.devices.length === 0 || roster.users.length === 0 ? (
                  <div className="p-3 text-sm text-stone-500">No active devices or users to show.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="user-device-matrix-table">
                      <thead className="bg-stone-100 text-stone-700 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 whitespace-nowrap">User</th>
                          <th className="text-left px-3 py-2 whitespace-nowrap">PIN</th>
                          {roster.devices.map(d => (
                            <th key={d.serial_number} className="text-center px-3 py-2 whitespace-nowrap" title={d.serial_number}>
                              <div className="font-mono">{d.branch || '—'}</div>
                              <div className="text-[10px] text-stone-500 font-normal">{d.serial_number}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {roster.users.map(u => (
                          <tr key={u.user_id} className="border-t border-stone-100 hover:bg-stone-50">
                            <td className="px-3 py-1.5">
                              <div className="font-medium text-stone-800">{u.display_name}</div>
                              <div className="text-[10px] text-stone-500">{u.username} · {u.role}</div>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-stone-700">{u.biometric_pin || '—'}</td>
                            {roster.devices.map(d => {
                              const cell = u.cells[d.serial_number] || { status: 'not_queued' };
                              const s = cell.status;
                              const bg =
                                s === 'loaded'     ? 'bg-emerald-100 text-emerald-800' :
                                s === 'pending'    ? 'bg-amber-100 text-amber-800'     :
                                s === 'failed'     ? 'bg-rose-100 text-rose-800'       :
                                s === 'deleted'    ? 'bg-stone-100 text-stone-500'     :
                                                     'bg-stone-50 text-stone-400';
                              const label =
                                s === 'loaded'     ? '✓' :
                                s === 'pending'    ? '…' :
                                s === 'failed'     ? '✗' :
                                s === 'deleted'    ? 'del' :
                                                     '—';
                              return (
                                <td key={d.serial_number} className="px-3 py-1.5 text-center">
                                  <span className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded ${bg}`}
                                    title={s + (cell.sent_at ? ` (sent ${cell.sent_at})` : cell.created_at ? ` (queued ${cell.created_at})` : '')}
                                    data-testid={`matrix-cell-${u.username}-${d.serial_number}`}>
                                    {label}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-stone-50 border-t border-stone-200 text-stone-600">
                        <tr>
                          <td className="px-3 py-2" colSpan={2}><b>{roster.users.length}</b> user(s)</td>
                          {roster.devices.map(d => {
                            const counts = roster.users.reduce((acc, u) => {
                              const st = (u.cells[d.serial_number] || {}).status || 'not_queued';
                              acc[st] = (acc[st] || 0) + 1;
                              return acc;
                            }, {});
                            return (
                              <td key={d.serial_number} className="px-3 py-2 text-center whitespace-nowrap">
                                <span className="text-emerald-700">{counts.loaded || 0}</span>
                                <span className="text-stone-400"> / </span>
                                <span className="text-amber-700">{counts.pending || 0}</span>
                                <span className="text-stone-400"> / </span>
                                <span className="text-stone-500">{counts.not_queued || 0}</span>
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Per-user punch trace diagnostic */}
            {traceOpen && (
              <div className="border border-stone-200 rounded-lg overflow-hidden mb-3" data-testid="user-trace-panel">
                <div className="bg-sky-50 border-b border-sky-200 px-3 py-2 text-xs text-sky-900 flex flex-wrap items-center gap-2">
                  <b>Trace a user's punches</b>
                  <span>Enter a username, biometric PIN, or employee code. Shows what arrived on our listener and what got turned into attendance records.</span>
                </div>
                <div className="p-3 flex flex-wrap items-center gap-2 bg-white">
                  <Input
                    className="max-w-sm"
                    placeholder="username, PIN, or employee code"
                    value={traceUsername}
                    onChange={e => setTraceUsername(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') runTrace(); }}
                    data-testid="trace-user-input" />
                  <Button size="sm" onClick={runTrace} disabled={traceLoading} data-testid="trace-user-run">
                    <RefreshCw className={`w-3 h-3 mr-1 ${traceLoading ? 'animate-spin' : ''}`} />
                    Trace
                  </Button>
                </div>
                {trace && (
                  <div className="p-3 bg-stone-50 border-t border-stone-100 space-y-3 text-xs">
                    {/* User summary */}
                    <div>
                      <div className="font-semibold text-stone-700 mb-1">User</div>
                      {trace.user ? (
                        <div className="bg-white rounded border border-stone-200 p-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                          <div><span className="text-stone-500">Username:</span> <b>{trace.user.username}</b></div>
                          <div><span className="text-stone-500">Name:</span> {trace.user.first_name} {trace.user.last_name}</div>
                          <div><span className="text-stone-500">Role:</span> {trace.user.role}</div>
                          <div><span className="text-stone-500">Active:</span> {String(trace.user.is_active)}</div>
                          <div><span className="text-stone-500">PIN:</span> <b>{trace.user.biometric_pin ?? '—'}</b></div>
                          <div><span className="text-stone-500">TT ID:</span> {trace.user.timetec_user_id ?? '—'}</div>
                          <div><span className="text-stone-500">Emp code:</span> {trace.user.employee_code ?? '—'}</div>
                          <div><span className="text-stone-500">Branch:</span> {trace.user.branch ?? (trace.user.branches || []).join(', ') ?? '—'}</div>
                        </div>
                      ) : (
                        <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-700">No user matched.</div>
                      )}
                    </div>

                    {/* Diagnostic hints */}
                    {trace.hints && trace.hints.length > 0 && (
                      <div>
                        <div className="font-semibold text-stone-700 mb-1">What's wrong</div>
                        <ul className="bg-amber-50 border border-amber-200 rounded p-2 space-y-1 list-disc ml-5 text-amber-900">
                          {trace.hints.map((h, i) => (<li key={i}>{h}</li>))}
                        </ul>
                      </div>
                    )}

                    {/* Punches counts */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="bg-white rounded border border-stone-200 p-2">
                        <div className="text-stone-500">Mappings</div>
                        <div className="font-mono text-lg text-stone-800">{trace.mappings?.length ?? 0}</div>
                      </div>
                      <div className="bg-white rounded border border-stone-200 p-2">
                        <div className="text-stone-500">Punches attributed</div>
                        <div className="font-mono text-lg text-emerald-700">{trace.punches_attributed?.length ?? 0}</div>
                      </div>
                      <div className="bg-white rounded border border-stone-200 p-2">
                        <div className="text-stone-500">Punches UNATTRIBUTED</div>
                        <div className={`font-mono text-lg ${trace.punches_unattributed?.length ? 'text-rose-700' : 'text-stone-800'}`}>
                          {trace.punches_unattributed?.length ?? 0}
                        </div>
                      </div>
                      <div className="bg-white rounded border border-stone-200 p-2">
                        <div className="text-stone-500">Attendance records</div>
                        <div className="font-mono text-lg text-stone-800">{trace.attendance_records?.length ?? 0}</div>
                      </div>
                    </div>

                    {/* Sample punches */}
                    {trace.punches_attributed && trace.punches_attributed.length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-stone-600">Latest attributed punches ({trace.punches_attributed.length})</summary>
                        <ul className="ml-4 mt-1 list-disc text-stone-700">
                          {trace.punches_attributed.slice(0, 10).map((p, i) => (
                            <li key={i}>
                              <b>{p.punch_time}</b> · uid={p.biometric_uid} · device={p.device_serial}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {trace.punches_unattributed && trace.punches_unattributed.length > 0 && (
                      <details open>
                        <summary className="cursor-pointer text-rose-700 font-medium">Unattributed punches - fix these ({trace.punches_unattributed.length})</summary>
                        <ul className="ml-4 mt-1 list-disc text-rose-700">
                          {trace.punches_unattributed.slice(0, 10).map((p, i) => (
                            <li key={i}>
                              <b>{p.punch_time}</b> · uid={p.biometric_uid} · device={p.device_serial}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
            <table className="w-full text-sm" data-testid="devices-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Serial</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Branch</th>
                  <th className="text-left px-3 py-2">Firmware</th>
                  <th className="text-left px-3 py-2">Last IP</th>
                  <th className="text-left px-3 py-2">Last seen</th>
                  <th className="text-right px-3 py-2">Active</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-stone-400 py-8">
                    No devices have connected yet. Point a device at <code>{(window?.location?.origin || '').replace(/\/$/, '')}/api</code> and reboot it, or use <b>Add device</b> to pre-register it with a branch.
                  </td></tr>
                )}
                {devices.map(d => (
                  <tr key={d.serial_number} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`device-row-${d.serial_number}`}>
                    <td className="px-3 py-2">
                      {d.online
                        ? <span className="text-emerald-700 text-xs inline-flex items-center"><Wifi className="w-3 h-3 mr-1" /> Online</span>
                        : <span className="text-stone-400 text-xs inline-flex items-center"><WifiOff className="w-3 h-3 mr-1" /> Offline</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{d.serial_number}</td>
                    <td className="px-3 py-2">
                      <input
                        defaultValue={d.device_name}
                        onBlur={(e) => { if (e.target.value !== d.device_name) renameDevice(d.serial_number, e.target.value); }}
                        className="border border-stone-200 rounded px-2 py-1 text-sm w-48"
                        data-testid={`rename-${d.serial_number}`}
                      />
                    </td>
                    <td className="px-3 py-2" data-testid={`device-branch-cell-${d.serial_number}`}>
                      <Select value={d.branch || '__unset__'} onValueChange={(v) => setDeviceBranch(d.serial_number, v === '__unset__' ? '' : v)}>
                        <SelectTrigger className={`w-40 h-8 text-xs ${d.branch ? '' : 'border-amber-400 text-amber-700'}`}
                          data-testid={`device-branch-select-${d.serial_number}`}>
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset__" className="text-stone-400">Unassigned</SelectItem>
                          {branches.map(b => (
                            <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-600">{d.firmware || '-'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-stone-600">{d.ip_last_seen || '-'}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(d.last_seen_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => toggleDeviceActive(d)}
                        data-testid={`toggle-active-${d.serial_number}`}
                        className={d.is_active ? 'text-emerald-700' : 'text-stone-400'}>
                        {d.is_active ? 'Active' : 'Disabled'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---------------- PUNCHES ---------------- */}
        <TabsContent value="punches">
          <div className="bg-white rounded-xl border border-stone-200 mb-3 p-3 flex items-center gap-3 flex-wrap">
            <Label className="text-xs">User</Label>
            <Select value={punchUserFilter} onValueChange={setPunchUserFilter}>
              <SelectTrigger className="w-48" data-testid="punch-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>)}
              </SelectContent>
            </Select>
            <Label className="text-xs">Device</Label>
            <Select value={punchDeviceFilter} onValueChange={setPunchDeviceFilter}>
              <SelectTrigger className="w-56" data-testid="punch-device-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {devices.map(d => <SelectItem key={d.serial_number} value={d.serial_number}>{d.device_name || d.serial_number}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchPunches}>
              <RefreshCw className={`w-3 h-3 mr-1 ${punchesLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <div className="text-xs text-stone-500 ml-auto">auto-refresh 10s</div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="punches-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">Time</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">UID</th>
                  <th className="text-left px-3 py-2">Device</th>
                  <th className="text-left px-3 py-2">State</th>
                  <th className="text-left px-3 py-2">Verify</th>
                </tr>
              </thead>
              <tbody>
                {punches.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-stone-400 py-8">No punches yet.</td></tr>
                )}
                {punches.map(p => (
                  <tr key={p.id} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(p.punch_time)}</td>
                    <td className="px-3 py-2">
                      {p.user_id
                        ? <span className="text-emerald-700">{userMap[p.user_id]?.username || p.user_id}</span>
                        : <span className="text-amber-600 text-xs">unmapped</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{p.biometric_uid}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.device_serial}</td>
                    <td className="px-3 py-2">{fmtPunchState(p.punch_state)}</td>
                    <td className="px-3 py-2 text-xs text-stone-600">{fmtVerify(p.verify_mode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---------------- QUEUE ---------------- */}
        <TabsContent value="queue">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {[
              { label: 'Pending', value: queue.stats?.PENDING || 0, color: 'bg-amber-100 text-amber-800', testid: 'queue-pending-count' },
              { label: 'Processing', value: queue.stats?.PROCESSING || 0, color: 'bg-blue-100 text-blue-800', testid: 'queue-processing-count' },
              { label: 'Completed', value: queue.stats?.COMPLETED || 0, color: 'bg-emerald-100 text-emerald-800', testid: 'queue-completed-count' },
              { label: 'Failed (DLQ)', value: queue.stats?.FAILED || 0, color: 'bg-rose-100 text-rose-800', testid: 'queue-failed-count' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl p-4 ${s.color}`} data-testid={s.testid}>
                <div className="text-xs uppercase tracking-wide opacity-80">{s.label}</div>
                <div className="text-3xl font-bold mt-1">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-stone-200 mb-3 overflow-hidden">
            <div className="px-4 py-2 flex items-center justify-between border-b border-stone-200">
              <div className="font-medium text-sm">Failed packets (DLQ)</div>
              <Button variant="outline" size="sm" onClick={fetchQueue}>
                <RefreshCw className={`w-3 h-3 mr-1 ${queueLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            <table className="w-full text-sm" data-testid="dlq-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Device</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Retries</th>
                  <th className="text-left px-3 py-2">Error</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(queue.failed || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-stone-400 py-6">No failed packets 🎉</td></tr>
                )}
                {(queue.failed || []).map(f => (
                  <tr key={f.id} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(f.received_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{f.device_serial}</td>
                    <td className="px-3 py-2 text-xs">{f.table_type}</td>
                    <td className="px-3 py-2 text-xs">{f.retry_count}</td>
                    <td className="px-3 py-2 text-xs text-rose-700 max-w-xs truncate" title={f.error_log}>{f.error_log}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewPacket(f)}>View raw</Button>
                      <Button size="sm" variant="outline" onClick={() => retryPacket(f.id)} data-testid={`retry-packet-${f.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Retry
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-600"
                        onClick={() => discardPacket(f.id)} data-testid={`discard-packet-${f.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-stone-200 font-medium text-sm">Recent packets</div>
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Device</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Records</th>
                </tr>
              </thead>
              <tbody>
                {(queue.recent || []).map(r => (
                  <tr key={r.id} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.received_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.device_serial}</td>
                    <td className="px-3 py-2 text-xs">{r.table_type}</td>
                    <td className="px-3 py-2 text-xs">{r.status}</td>
                    <td className="px-3 py-2 text-right text-xs">{r.records_inserted ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---------------- MAPPINGS ---------------- */}
        <TabsContent value="mappings">
          {/* Helper banner explaining auto-resolution */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3 flex flex-wrap items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0" />
            <div className="flex-1 text-sm text-emerald-900">
              <b>Auto-resolution is on.</b> Punches automatically match to app users whose <b>TimeTec User ID</b> equals the device UID — no manual mapping needed.
              Use this tab only to override that match (e.g. when a UID was reassigned) or to confirm a specific user→device pairing.
            </div>
            <Button size="sm" variant="outline" onClick={importFromTimetec} data-testid="import-timetec-button">
              <Link2 className="w-3 h-3 mr-1" /> Snapshot from TimeTec IDs
            </Button>
            <Button size="sm" variant="ghost" onClick={backfillUserIds} data-testid="backfill-button">
              <RefreshCw className="w-3 h-3 mr-1" /> Re-resolve unmapped
            </Button>
          </div>

          {/* Unmapped UIDs banner */}
          {uids.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
              <div className="text-sm font-medium text-amber-900 mb-2">
                {uids.length} unmapped UID(s) seen in recent punches
              </div>
              <div className="flex flex-wrap gap-2">
                {uids.slice(0, 20).map(u => (
                  <button
                    key={`${u.biometric_uid}-${u.device_serial}`}
                    onClick={() => quickMapFromUid(u)}
                    className="bg-white border border-amber-300 rounded px-2 py-1 text-xs hover:bg-amber-100"
                    data-testid={`unmapped-uid-${u.biometric_uid}`}>
                    UID <b>{u.biometric_uid}</b> · {u.count} punch{u.count === 1 ? '' : 'es'} · {u.device_serial}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add mapping form */}
          <div className="bg-white rounded-xl border border-stone-200 p-3 mb-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div>
              <Label className="text-xs">Biometric UID</Label>
              <Input value={newMapping.biometric_uid}
                onChange={e => setNewMapping({ ...newMapping, biometric_uid: e.target.value })}
                placeholder="e.g. 1023" data-testid="mapping-uid-input" />
            </div>
            <div>
              <Label className="text-xs">App user</Label>
              <Select value={newMapping.user_id}
                onValueChange={v => setNewMapping({ ...newMapping, user_id: v })}>
                <SelectTrigger data-testid="mapping-user-select"><SelectValue placeholder="Select user…" /></SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.is_active !== false).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Device (optional)</Label>
              <Select value={newMapping.device_serial || 'all'}
                onValueChange={v => setNewMapping({ ...newMapping, device_serial: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All devices</SelectItem>
                  {devices.map(d => (
                    <SelectItem key={d.serial_number} value={d.serial_number}>{d.device_name || d.serial_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createMapping} data-testid="add-mapping-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Add mapping
            </Button>
          </div>

          {/* Mappings list */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="px-4 py-2 flex items-center justify-between border-b border-stone-200">
              <div className="relative w-72">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-stone-400" />
                <Input value={mappingSearch} onChange={e => setMappingSearch(e.target.value)}
                  placeholder="Search…" className="pl-8" />
              </div>
              <Button variant="outline" size="sm" onClick={fetchMappings}>
                <RefreshCw className={`w-3 h-3 mr-1 ${mappingsLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            <table className="w-full text-sm" data-testid="mapping-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2">UID</th>
                  <th className="text-left px-3 py-2">App user</th>
                  <th className="text-left px-3 py-2">Device</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-stone-400 py-6">No mappings yet.</td></tr>
                )}
                {filteredMappings.map(m => (
                  <tr key={m.id} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-mono text-xs">{m.biometric_uid}</td>
                    <td className="px-3 py-2">{m.username || userMap[m.user_id]?.username || m.user_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{m.device_serial || 'All'}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(m.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteMapping(m.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Raw packet viewer */}
      <Dialog open={!!viewPacket} onOpenChange={(o) => { if (!o) setViewPacket(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Raw packet</DialogTitle></DialogHeader>
          {viewPacket && (
            <div>
              <div className="text-xs text-stone-500 mb-2">
                {viewPacket.device_serial} · {viewPacket.table_type} · {fmtDate(viewPacket.received_at)}
              </div>
              <pre className="bg-stone-900 text-stone-100 p-3 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap">
                {viewPacket.raw_packet || '(empty)'}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add device manually */}
      <Dialog open={addDeviceOpen} onOpenChange={(o) => { if (!o) setAddDeviceOpen(false); }}>
        <DialogContent data-testid="add-bio-device-dialog">
          <DialogHeader>
            <DialogTitle>Add biometric device</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
              Pre-register a clocking device so punches from it can be linked to a
              branch immediately - even before the device first connects.
              The <b>Branch</b> is required so every punch lands in the right location.
            </div>
            <div>
              <Label className="text-xs">Serial number <span className="text-rose-600">*</span></Label>
              <Input
                value={addDeviceForm.serial_number}
                onChange={(e) => setAddDeviceForm({ ...addDeviceForm, serial_number: e.target.value })}
                placeholder="e.g. CNYJ231260135"
                data-testid="add-bio-device-serial"
              />
            </div>
            <div>
              <Label className="text-xs">Device name</Label>
              <Input
                value={addDeviceForm.device_name}
                onChange={(e) => setAddDeviceForm({ ...addDeviceForm, device_name: e.target.value })}
                placeholder="Optional (defaults to serial)"
                data-testid="add-bio-device-name"
              />
            </div>
            <div>
              <Label className="text-xs">Branch <span className="text-rose-600">*</span></Label>
              <Select
                value={addDeviceForm.branch}
                onValueChange={(v) => setAddDeviceForm({ ...addDeviceForm, branch: v })}
              >
                <SelectTrigger data-testid="add-bio-device-branch">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setAddDeviceOpen(false)}>Cancel</Button>
              <Button
                onClick={submitAddDevice}
                disabled={addDeviceSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="add-bio-device-submit"
              >
                {addDeviceSaving ? 'Saving…' : 'Add device'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminBiometricTab;
